import React, {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { ButtonToolbar, Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import Icon from 'utility/icomoon';

interface Props {
  buttons: any[];
  className?: string;
  position?: 'right' | 'between' | 'left';
  style?: React.CSSProperties;
  buttonFilter?: React.ReactElement;
}

// Los anchos medidos ya incluyen el `margin-left: 12px` de `.btn-toolbar .btn`,
// así que no se vuelve a sumar separación. Tolerancia para redondeo sub-pixel.
const WIDTH_TOLERANCE = 2;
const FALLBACK_MORE_WIDTH = 44;

/**
 * Toolbar responsive: muestra los botones que caben y manda el resto a un
 * dropdown de overflow. Conserva visibles los de la derecha (acciones CRUD).
 */
const Toolbar = (props: Props) => {
  const { buttons, className = '', position = 'right', buttonFilter } = props;

  const groupRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const widthsRef = useRef<number[]>([]);

  const [visibleCount, setVisibleCount] = useState(buttons.length);
  const [measuring, setMeasuring] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Calcula cuántos botones caben usando los anchos cacheados en `widthsRef`.
  const computeVisible = useCallback(() => {
    const group = groupRef.current;
    const widths = widthsRef.current;
    if (!group || widths.length === 0) return;

    const available = group.clientWidth;
    if (available === 0) return;

    const totalAll = widths.reduce((acc, w) => acc + w, 0);
    if (totalAll <= available + WIDTH_TOLERANCE) {
      setVisibleCount(widths.length);
      return;
    }

    // Hay overflow: se itera desde el final para conservar los botones de la derecha.
    const moreWidth = moreRef.current?.getBoundingClientRect().width ?? FALLBACK_MORE_WIDTH;

    let used = 0;
    let count = 0;
    for (let i = widths.length - 1; i >= 0; i--) {
      if (used + widths[i] + moreWidth <= available + WIDTH_TOLERANCE) {
        used += widths[i];
        count++;
      } else {
        break;
      }
    }
    setVisibleCount(count);
  }, []);

  // Re-medir cuando cambia la cantidad de botones.
  useLayoutEffect(() => {
    setMeasuring(true);
    setVisibleCount(buttons.length);
  }, [buttons.length]);

  // Fase de medición: todos los botones en línea para capturar su ancho real.
  useLayoutEffect(() => {
    if (!measuring) return;
    widthsRef.current = buttons.map((_, i) => {
      const el = itemRefs.current[i];
      return el ? el.getBoundingClientRect().width : 0;
    });
    setMeasuring(false);
    computeVisible();
  }, [measuring, computeVisible, buttons]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (!measuring) computeVisible();
    });
    observer.observe(group);
    return () => observer.disconnect();
  }, [computeVisible, measuring]);

  const overflowEnd = buttons.length - visibleCount;
  const visibleButtons = measuring ? buttons : buttons.slice(overflowEnd);
  const overflowButtons = measuring ? [] : buttons.slice(0, overflowEnd);
  const hasOverflow = overflowButtons.length > 0;

  return (
    <ButtonToolbar
      className={`${
        position == 'right'
          ? 'justify-content-end'
          : position == 'between'
          ? 'justify-content-between'
          : 'justify-content-start'
      } ${className}`}
      style={props.style}
    >
      <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
        {buttonFilter && <div className="btn-filter-toolbar">{buttonFilter}</div>}
        <div className="group-btn-toolbar" ref={groupRef}>
          {hasOverflow && (
            <div ref={moreRef} className="toolbar-overflow">
              <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen((o) => !o)}>
                <DropdownToggle
                  id="toolbarOverflowToggle"
                  className="btn-icon toolbar-more-toggle"
                  color="secondary"
                >
                  <Icon icon="MoreHorizontal" size={16} />
                </DropdownToggle>
                <DropdownMenu className="toolbar-overflow-menu">
                  <div className="toolbar-overflow-items">
                    {overflowButtons.map((child, index) => (
                      <Fragment key={index}>{child}</Fragment>
                    ))}
                  </div>
                </DropdownMenu>
              </Dropdown>
            </div>
          )}

          {visibleButtons.map((child, index) =>
            measuring ? (
              <div
                key={index}
                className="toolbar-measure-item"
                ref={(el) => (itemRefs.current[index] = el)}
              >
                {child}
              </div>
            ) : (
              <Fragment key={index}>{child}</Fragment>
            )
          )}
        </div>
      </div>
    </ButtonToolbar>
  );
};

export default Toolbar;
